import type { ReactElement } from "react";

import { type FormUsage, FormUsagePanel } from "../../components/form-usage-panel.js";
import { StorageGauge, type StorageUsage } from "../../components/storage-gauge.js";

import type { PausedPayment } from "./paused-payment-notice.js";

/**
 * 契約の中身（<c>GET /api/tenants/{契約}</c> の <c>contract</c>。サーバの TenantContractDetail と同じ欄）。
 * <b>契約者にだけサーバが付ける</b>（管理者・一般の利用者には null）。
 */
export interface TenantContract {
    readonly planDisplayName: string;
    readonly qty: number;
    readonly storageSize: number;
    readonly extensionTitles: string;
    /**
     * 月額（税込）。<b>金額を見せない契約者にはサーバが null にする</b>（請求書を見られる人の指定があり、そこに入っていない契約者。
     * Implem.Portal.Backend の docs/features/10 §1-2）。null なら月額の行ごと出さない。
     */
    readonly amountLabel: string | null;
    /** 課金が始まる日からの月額。無料期間の間だけ。金額を見せない契約者にも null。 */
    readonly billingStartsLabel: string | null;
    /**
     * 無償利用・月額以外の契約のとき、月額の代わりに出す文言（「無償利用の契約」「月額以外の契約」。サーバが作る）。
     * <b>この契約ではサーバが月額・課金の開始日・無料期間を返さない</b>（Implem.Portal.Backend の docs/features/10 §1-2）。ほかの契約は null。
     */
    readonly exemptLabel?: string | null;
    readonly trialLabel: string | null;
    readonly trial: { readonly isEnding: boolean } | null;
    /** ストレージの使用量（サーバの StorageGauge と同じ欄）。サーバが付けないときは null。 */
    readonly storageUsage: StorageUsage | null;
    /** フォームの月ごとの受付件数。フォーム機能を付けている契約だけ（サーバが決める）。ほかは null。 */
    readonly formUsage: FormUsage | null;
    /**
     * 「プラン変更」を出すか（サーバが決める）。<b>無償利用・月額以外の契約は false</b>——会員が自分で変えられないので、
     * ボタンの代わりに運営へのお問い合わせを案内する（Implem.Portal.Backend の docs/features/08 §1）。
     */
    readonly canChangePlan: boolean;
    /** 「カード情報の変更」を出すか（クレジットカード払いの契約の契約者だけ。サーバが決める）。 */
    readonly canChangeCard: boolean;
    /** 「接続を許可する IP アドレス」を出すか（接続元 IP の制限を契約している契約の契約者だけ。サーバが決める）。 */
    readonly canEditIpRestriction: boolean;
    /** お支払いが止まっている印。カード払いの契約で定期課金が止まっているときだけサーバが付ける。ほかは null。 */
    readonly paymentPaused: PausedPayment | null;
}

/** 契約の中身の欄（プラン・ユーザ数・ストレージ・オプション・月額）。dl の中に並べる。<b>文言はサーバが作る</b>。月額はサーバが返したときだけ。 */
export function ContractFields({ contract }: { readonly contract: TenantContract }): ReactElement {
    return (
        <>
            <dt className="text-slate-500">プラン</dt>
            <dd>{contract.planDisplayName}</dd>
            <dt className="text-slate-500">ユーザ数</dt>
            <dd>{contract.qty} 名</dd>
            <dt className="text-slate-500">追加ストレージ</dt>
            <dd>{contract.storageSize} GB</dd>
            {/* 使用量の文言と割合はサーバが作る。付けていない契約（null）には行ごと出さない */}
            {contract.storageUsage === null
                ? null
                : (
                    <>
                        <dt className="text-slate-500">ストレージ使用量</dt>
                        <dd><StorageGauge usage={contract.storageUsage} /></dd>
                    </>
                )}
            <dt className="text-slate-500">オプション</dt>
            <dd className="break-words">{contract.extensionTitles}</dd>
            {/* 無償利用・月額以外の契約は、月額の代わりに契約の種別の文言だけを出す（金額はサーバが返さない） */}
            {contract.exemptLabel === undefined || contract.exemptLabel === null
                ? null
                : (
                    <>
                        <dt className="text-slate-500">契約の種別</dt>
                        <dd data-contract-exempt="">{contract.exemptLabel}</dd>
                    </>
                )}
            {/* 金額を見せない契約者（null）には行ごと出さない。課金が始まる日からの月額も同じ行にあるので一緒に消える */}
            {contract.amountLabel === null
                ? null
                : (
                    <>
                        <dt className="text-slate-500">月額（税込）</dt>
                        <dd>
                            <span className="text-base font-bold text-slate-900">{contract.amountLabel}</span>
                            {contract.billingStartsLabel === null
                                ? null
                                : <span className="text-slate-500">（{contract.billingStartsLabel}）</span>}
                        </dd>
                    </>
                )}
        </>
    );
}

/** 無料期間の札と、フォームの受付件数。どちらもサーバが付けたときだけ出す。 */
export function ContractNotes({ contract }: { readonly contract: TenantContract }): ReactElement {
    return (
        <>
            {contract.trial === null || contract.trialLabel === null
                ? null
                : (
                    <p className="mb-4">
                        <span className={contract.trial.isEnding ? "badge-warning" : "badge-info"}>
                            {contract.trialLabel}
                        </span>
                    </p>
                )}
            {contract.formUsage === null ? null : <FormUsagePanel usage={contract.formUsage} />}
        </>
    );
}
